import React, { useState } from "react";
import { supabase } from "../supabaseClient";
import DialogImport from "./DialogImport";
import { useNavigate } from "react-router-dom";

function AdminUI() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [ignoreFile, setIgnoreFile] = useState(null);
  const [wordListFile, setWordListFile] = useState(null);
  const [wordListName, setWordListName] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = async () => {
    if (!ignoreFile || !wordListFile || !wordListName) {
      alert("Please provide all required files and name");
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      // Read ignore words file
      const ignoreWordsText = await ignoreFile.text();
      const ignoreWords = ignoreWordsText
        .split("\n")
        .map((word) => word.trim())
        .filter((word) => word);

      // Read word list file
      const wordListText = await wordListFile.text();
      const words = wordListText
        .split("\n")
        .map((word) => word.trim())
        .filter((word) => word);

      // Check for duplicate words before importing
      const duplicateWords = words.filter(
        (word, index) => words.indexOf(word) !== index,
      );
      const uniqueDuplicates = [...new Set(duplicateWords)];
      if (uniqueDuplicates.length > 0) {
        setIsImporting(false);
        alert("Duplicate words found: " + uniqueDuplicates.join(", "));
        return;
      }

      let stats = {
        total: words.length,
        ignored: 0,
        newWords: 0,
        relationships: 0,
      };

      // Create wordlist record
      const { data: wordlist, error: wordlistError } = await supabase
        .from("wordlists")
        .insert([{ name: wordListName }])
        .select()
        .single();

      if (wordlistError) throw wordlistError;

      // Process each word
      for (const word of words) {
        // Skip if in ignore list
        if (ignoreWords.includes(word)) {
          stats.ignored++;
          continue;
        }

        // Check if word exists
        const { data: existingWord } = await supabase
          .from("words")
          .select("word_id")
          .eq("word_text", word)
          .single();

        let wordId;
        if (!existingWord) {
          // Insert new word
          const { data: newWord, error: wordError } = await supabase
            .from("words")
            .insert([{ word_text: word }])
            .select()
            .single();

          if (wordError) throw wordError;
          wordId = newWord.word_id;
          stats.newWords++;
        } else {
          wordId = existingWord.word_id;
        }

        // Create relationship
        const { error: relationshipError } = await supabase
          .from("wordlist_words")
          .insert([
            {
              wordlist_id: wordlist.wordlist_id,
              word_id: wordId,
            },
          ]);

        if (relationshipError) throw relationshipError;
        stats.relationships++;
      }

      setImportResult(stats);
      console.log("Import completed:", stats);
    } catch (error) {
      console.error("Error importing word list:", error);
      const errorMessage = error.message || JSON.stringify(error, null, 2);
      alert("Error importing word list: " + errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-base-100">
      <header className="bg-primary text-white py-6 shadow-md">
        <div className="container mx-auto px-4 flex items-center">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-white mr-6"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-3xl font-bold flex-1 text-center">
            Admin Console
          </h1>
          <div className="w-[76px]"></div> {/* Spacer to balance the layout */}
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Word List Management</h2>
            <p className="mb-4 text-gray-600">
              Import new word lists into the system.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="btn btn-primary w-full"
            >
              Import Word List
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Review Dialogs</h2>
            <p className="mb-4 text-gray-600">
              View and manage conversation dialogs.
            </p>
            <button
              onClick={() => navigate("/review-dialogs")}
              className="btn btn-primary w-full"
            >
              Review Dialogs
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">Generate Missing Dialogs</h2>
            <p className="mb-4 text-gray-600">
              Automatically create dialogs, images and audio for words with less
              than three dialogs.
            </p>
            <button
              onClick={() => navigate("/generate-missing-dialogs")}
              className="btn btn-primary w-full"
            >
              Generate Missing Dialogs
            </button>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-2">Zamboni Scan (All Words)</h2>
            <p className="mb-4 text-gray-600">
              Scan every word and repair or generate missing dialogs/assets.
            </p>
            <button
              onClick={() => navigate("/zamboni-scan")}
              className="btn btn-secondary w-full"
            >
              Run Zamboni Scan
            </button>
          </div>
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg max-w-2xl w-full">
            <h3 className="text-2xl font-bold mb-4">Import Word List</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ignore Words CSV (no header)
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setIgnoreFile(e.target.files[0])}
                  className="file-input file-input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Word List CSV (no header)
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setWordListFile(e.target.files[0])}
                  className="file-input file-input-bordered w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Word List Name
                </label>
                <input
                  type="text"
                  value={wordListName}
                  onChange={(e) => setWordListName(e.target.value)}
                  className="input input-bordered w-full"
                  placeholder="Enter word list name"
                />
              </div>

              {importResult && (
                <div className="bg-green-100 p-4 rounded-lg">
                  <p className="text-green-800">
                    Import successful!
                    <br />
                    Total words: {importResult.total}
                    <br />
                    Ignored: {importResult.ignored}
                    <br />
                    New words added: {importResult.newWords}
                    <br />
                    Relationships created: {importResult.relationships}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setImportResult(null);
                    setIgnoreFile(null);
                    setWordListFile(null);
                    setWordListName("");
                  }}
                  className="btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  className="btn btn-primary"
                >
                  {isImporting ? "Importing..." : "Import"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DialogImport
        isOpen={showDialogImport}
        onClose={() => setShowDialogImport(false)}
      />
    </div>
  );
}

export default AdminUI;
